import axios from 'axios';
import createSignedRequest from './createSignedRequest';

const { apiURL } = require('./apiURL');

export const Types = {
  SET_DASHBOARD: 'CONSULTANT_DASHBOARD_SET',
  SET_HEALTH: 'CONSULTANT_DASHBOARD_HEALTH',
  SET_IMPACT: 'CONSULTANT_DASHBOARD_IMPACT',
  SET_OWNER_FILTER: 'CONSULTANT_DASHBOARD_OWNER_FILTER',
  LOADING: 'CONSULTANT_DASHBOARD_LOADING',
  ERROR: 'CONSULTANT_DASHBOARD_ERROR',
};

const setLoading = (key, value) => ({ type: Types.LOADING, key, value });
const setError = (error) => ({ type: Types.ERROR, error });

const resolveFirmOrgId = (state) => {
  const raw = state.currentOrganisation || state.profile?.details?.consultantAdmin || '';
  const id = String(raw || '').trim();
  if (!id || id === 'false' || id === 'null' || id === 'undefined') return '';
  return id;
};

const orgHeader = (state) => ({
  Organisation: resolveFirmOrgId(state),
});

const clientOrgHeader = (state) => {
  const orgId = state.currentOrganisation || state.organisation?.details?.id;
  return { Organisation: String(orgId || '') };
};

const parseApiError = (e) => {
  const errBody = e?.response?.data || {};
  return {
    ok: false,
    error: errBody.error || errBody.code || errBody,
    message: errBody.message || errBody.error || e.message || 'Request failed',
    status: e?.response?.status,
  };
};

/**
 * Load Surface A operational dashboard.
 * @param {{ ownerUserId?: string|null, refresh?: boolean }} [opts]
 */
export const getConsultantDashboard = (opts = {}) => async (dispatch, getState) => {
  const orgId = resolveFirmOrgId(getState());
  if (!orgId) {
    // Avoid firing before the firm is selected — Organisation: "false" yields 403 and sticky zeros.
    return { ok: false, error: 'missing_organisation', message: 'No organisation selected' };
  }
  dispatch(setLoading('dashboard', true));
  dispatch(setError(null));
  try {
    const qs = new URLSearchParams();
    if (opts.ownerUserId) qs.set('ownerUserId', opts.ownerUserId);
    if (opts.refresh) qs.set('refresh', 'true');
    const q = qs.toString() ? `?${qs}` : '';
    const response = await axios(
      await createSignedRequest('GET', `${apiURL}/my/consultant/dashboard${q}`, null, orgHeader(getState())),
    );
    const data = response.data || {};
    dispatch({ type: Types.SET_DASHBOARD, payload: data });
    return { ok: true, data };
  } catch (e) {
    const result = parseApiError(e);
    dispatch(setError(result.message));
    return result;
  } finally {
    dispatch(setLoading('dashboard', false));
  }
};

/**
 * Load Surface B client health.
 * @param {string} orgId
 * @param {{ refresh?: boolean }} [opts]
 */
export const getClientHealth = (orgId, opts = {}) => async (dispatch, getState) => {
  dispatch(setLoading('health', true));
  dispatch(setError(null));
  try {
    const q = opts.refresh ? '?refresh=true' : '';
    const response = await axios(
      await createSignedRequest(
        'GET',
        `${apiURL}/my/consultant/clients/${orgId}/health${q}`,
        null,
        orgHeader(getState()),
      ),
    );
    const data = response.data || {};
    dispatch({ type: Types.SET_HEALTH, payload: data });
    return { ok: true, data };
  } catch (e) {
    const result = parseApiError(e);
    dispatch(setError(result.message));
    return result;
  } finally {
    dispatch(setLoading('health', false));
  }
};

/**
 * Load Surface C Impact & Wins.
 * @param {{ ownerUserId?: string|null, refresh?: boolean }} [opts]
 */
export const getConsultantImpact = (opts = {}) => async (dispatch, getState) => {
  const orgId = resolveFirmOrgId(getState());
  if (!orgId) {
    return { ok: false, error: 'missing_organisation', message: 'No organisation selected' };
  }
  dispatch(setLoading('impact', true));
  dispatch(setError(null));
  try {
    const qs = new URLSearchParams();
    if (opts.ownerUserId) qs.set('ownerUserId', opts.ownerUserId);
    if (opts.refresh) qs.set('refresh', 'true');
    const q = qs.toString() ? `?${qs}` : '';
    const response = await axios(
      await createSignedRequest('GET', `${apiURL}/my/consultant/impact${q}`, null, orgHeader(getState())),
    );
    const data = response.data || {};
    dispatch({ type: Types.SET_IMPACT, payload: data });
    return { ok: true, data };
  } catch (e) {
    const result = parseApiError(e);
    dispatch(setError(result.message));
    return result;
  } finally {
    dispatch(setLoading('impact', false));
  }
};

/**
 * Assign or clear client owner.
 * @param {string} orgId
 * @param {string|null} ownerUserId
 */
export const assignClientOwner = (orgId, ownerUserId) => async (dispatch, getState) => {
  dispatch(setLoading('owner', true));
  try {
    const response = await axios(
      await createSignedRequest(
        'PATCH',
        `${apiURL}/my/consultant/clients/${orgId}/owner`,
        JSON.stringify({ ownerUserId }),
        orgHeader(getState()),
      ),
    );
    return { ok: true, data: response.data };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading('owner', false));
  }
};

/**
 * Create a manual work item for a client (R-7701).
 */
export const createWorkItem = (clientOrgId, body = {}) => async (dispatch, getState) => {
  dispatch(setLoading('owner', true));
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/work-items`,
        JSON.stringify({ clientOrgId, ...body }),
        orgHeader(getState()),
      ),
    );
    return { ok: true, data: response.data, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading('owner', false));
  }
};

/** List consultant work items (mission control). */
export const listWorkItems = (params = {}) => async (dispatch, getState) => {
  const orgId = resolveFirmOrgId(getState());
  if (!orgId) {
    return { ok: false, error: 'missing_organisation', message: 'No organisation selected' };
  }
  try {
    const qs = new URLSearchParams();
    if (params.clientOrgId) qs.set('clientOrgId', params.clientOrgId);
    if (params.holder) qs.set('holder', params.holder);
    if (params.status) qs.set('status', params.status);
    if (params.includeDrafts) qs.set('includeDrafts', 'true');
    const q = qs.toString() ? `?${qs}` : '';
    const response = await axios(
      await createSignedRequest('GET', `${apiURL}/my/consultant/work-items${q}`, null, orgHeader(getState())),
    );
    return { ok: true, items: response.data?.items || [] };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Load one work item with timeline. */
export const getWorkItem = (workItemId) => async (dispatch, getState) => {
  const orgId = resolveFirmOrgId(getState());
  if (!orgId) {
    return { ok: false, error: 'missing_organisation', message: 'No organisation selected' };
  }
  try {
    const response = await axios(
      await createSignedRequest(
        'GET',
        `${apiURL}/my/consultant/work-items/${workItemId}`,
        null,
        orgHeader(getState()),
      ),
    );
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Patch work item fields / status / share. */
export const updateWorkItem = (workItemId, patch = {}) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'PATCH',
        `${apiURL}/my/consultant/work-items/${workItemId}`,
        JSON.stringify(patch),
        orgHeader(getState()),
      ),
    );
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Post Task Message (internal flag for consultants). */
export const postWorkItemMessage = (workItemId, body, internal = false, attachments = null) => async (
  dispatch,
  getState,
) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/work-items/${workItemId}/messages`,
        JSON.stringify({ body, internal, attachments }),
        orgHeader(getState()),
      ),
    );
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Presign + metadata for Task Message attachments or Work Item files (R-7713/R-7714). */
export const requestAttachmentUpload = (workItemId, fileMeta, opts = {}) => async (dispatch, getState) => {
  try {
    const client = !!opts.client;
    const path = client
      ? `${apiURL}/my/work-items/${workItemId}/attachment-upload`
      : `${apiURL}/my/consultant/work-items/${workItemId}/attachment-upload`;
    const headers = client ? clientOrgHeader(getState()) : orgHeader(getState());
    const response = await axios(
      await createSignedRequest('POST', path, JSON.stringify(fileMeta || {}), headers),
    );
    const attachments = response.data?.attachments || (response.data?.attachment ? [response.data.attachment] : []);
    return {
      ok: true,
      uploadUrl: response.data?.uploadUrl || attachments[0]?.uploadUrl,
      attachment: response.data?.attachment || attachments[0] || null,
      attachments,
      messageId: response.data?.messageId,
    };
  } catch (e) {
    return parseApiError(e);
  }
};

export const addWorkItemFiles = (workItemId, files, opts = {}) => async (dispatch, getState) => {
  try {
    const client = !!opts.client;
    const path = client
      ? `${apiURL}/my/work-items/${workItemId}/files`
      : `${apiURL}/my/consultant/work-items/${workItemId}/files`;
    const headers = client ? clientOrgHeader(getState()) : orgHeader(getState());
    const response = await axios(
      await createSignedRequest('POST', path, JSON.stringify({ files }), headers),
    );
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};

export const downloadWorkItemAttachment = (workItemId, attachmentId, opts = {}) => async (
  dispatch,
  getState,
) => {
  try {
    const client = !!opts.client;
    const path = client
      ? `${apiURL}/my/work-items/${workItemId}/attachments/${attachmentId}/download`
      : `${apiURL}/my/consultant/work-items/${workItemId}/attachments/${attachmentId}/download`;
    const headers = client ? clientOrgHeader(getState()) : orgHeader(getState());
    const response = await axios(await createSignedRequest('GET', path, null, headers));
    return { ok: true, url: response.data?.url, name: response.data?.name };
  } catch (e) {
    return parseApiError(e);
  }
};

export const removeWorkItemAttachment = (workItemId, attachmentId, opts = {}) => async (
  dispatch,
  getState,
) => {
  try {
    const client = !!opts.client;
    const path = client
      ? `${apiURL}/my/work-items/${workItemId}/attachments/${attachmentId}`
      : `${apiURL}/my/consultant/work-items/${workItemId}/attachments/${attachmentId}`;
    const headers = client ? clientOrgHeader(getState()) : orgHeader(getState());
    const response = await axios(await createSignedRequest('DELETE', path, JSON.stringify({}), headers));
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};


export const renameWorkItemAttachment = (workItemId, attachmentId, name, opts = {}) => async (
  dispatch,
  getState,
) => {
  try {
    const client = !!opts.client;
    const path = client
      ? `${apiURL}/my/work-items/${workItemId}/attachments/${attachmentId}`
      : `${apiURL}/my/consultant/work-items/${workItemId}/attachments/${attachmentId}`;
    const headers = client ? clientOrgHeader(getState()) : orgHeader(getState());
    const response = await axios(
      await createSignedRequest('PATCH', path, JSON.stringify({ name }), headers),
    );
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};

export const createSharedAsk = (body) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/work-items`,
        JSON.stringify(body || {}),
        clientOrgHeader(getState()),
      ),
    );
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Create chase Draft Message (approval-first). */
export const createChaseDraft = (workItemId, body) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/work-items/${workItemId}/draft-messages`,
        JSON.stringify({ body, source: 'chase' }),
        orgHeader(getState()),
      ),
    );
    return { ok: true, item: response.data?.item, draft: response.data?.draft };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Approve & post a Draft Message. */
export const approveChaseDraft = (workItemId, draftId) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/work-items/${workItemId}/draft-messages/${draftId}/approve`,
        JSON.stringify({}),
        orgHeader(getState()),
      ),
    );
    return { ok: true, item: response.data?.item, draft: response.data?.draft };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Toggle Responding-party Access on an Engagement. */
export const setRespondingPartyAccess = (clientOrgId, enabled) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'PATCH',
        `${apiURL}/my/consultant/engagements/${clientOrgId}/responding-party-access`,
        JSON.stringify({ enabled: !!enabled }),
        orgHeader(getState()),
      ),
    );
    return { ok: true, engagement: response.data?.engagement };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Projects (R-7708). */
export const listProjects = (clientOrgId) => async (dispatch, getState) => {
  if (!resolveFirmOrgId(getState())) {
    return { ok: false, error: 'missing_organisation', message: 'No organisation selected' };
  }
  try {
    const response = await axios(
      await createSignedRequest(
        'GET',
        `${apiURL}/my/consultant/projects?clientOrgId=${encodeURIComponent(clientOrgId)}`,
        null,
        orgHeader(getState()),
      ),
    );
    return { ok: true, projects: response.data?.projects || [] };
  } catch (e) {
    return parseApiError(e);
  }
};

export const createProject = (body) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/projects`,
        JSON.stringify(body || {}),
        orgHeader(getState()),
      ),
    );
    return { ok: true, project: response.data?.project };
  } catch (e) {
    return parseApiError(e);
  }
};

export const updateProject = (projectId, patch) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'PATCH',
        `${apiURL}/my/consultant/projects/${projectId}`,
        JSON.stringify(patch || {}),
        orgHeader(getState()),
      ),
    );
    return { ok: true, project: response.data?.project };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Time entries (R-7710). */
export const addTimeEntry = (workItemId, body) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/work-items/${workItemId}/time-entries`,
        JSON.stringify(body || {}),
        orgHeader(getState()),
      ),
    );
    return { ok: true, item: response.data?.item, entry: response.data?.entry };
  } catch (e) {
    return parseApiError(e);
  }
};

/**
 * Correct or remove a Time Entry (R-7710).
 * @param {string} workItemId
 * @param {string} entryId
 * @param {object} patch — fields to change, or `{ remove: true }`
 */
export const updateTimeEntry = (workItemId, entryId, patch) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'PATCH',
        `${apiURL}/my/consultant/work-items/${workItemId}/time-entries/${entryId}`,
        JSON.stringify(patch || {}),
        orgHeader(getState()),
      ),
    );
    return { ok: true, item: response.data?.item, entry: response.data?.entry };
  } catch (e) {
    return parseApiError(e);
  }
};

/** Client Shared asks list. */
export const listSharedAsks = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const orgId = state.currentOrganisation || state.organisation?.details?.id;
    const response = await axios(
      await createSignedRequest('GET', `${apiURL}/my/work-items`, null, {
        Organisation: String(orgId || ''),
      }),
    );
    return { ok: true, items: response.data?.items || [] };
  } catch (e) {
    return parseApiError(e);
  }
};

export const getSharedAsk = (workItemId) => async (dispatch, getState) => {
  try {
    const state = getState();
    const orgId = state.currentOrganisation || state.organisation?.details?.id;
    const response = await axios(
      await createSignedRequest('GET', `${apiURL}/my/work-items/${workItemId}`, null, {
        Organisation: String(orgId || ''),
      }),
    );
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};

export const updateSharedAsk = (workItemId, patch) => async (dispatch, getState) => {
  try {
    const state = getState();
    const orgId = state.currentOrganisation || state.organisation?.details?.id;
    const response = await axios(
      await createSignedRequest(
        'PATCH',
        `${apiURL}/my/work-items/${workItemId}`,
        JSON.stringify(patch || {}),
        { Organisation: String(orgId || '') },
      ),
    );
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};

export const replySharedAsk = (workItemId, body, attachments = null) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/work-items/${workItemId}/messages`,
        JSON.stringify({ body, attachments }),
        clientOrgHeader(getState()),
      ),
    );
    return { ok: true, item: response.data?.item };
  } catch (e) {
    return parseApiError(e);
  }
};

/**
 * Completeness sign-off (R-7901).
 */
export const signOffCompleteness = (clientOrgId) => async (dispatch, getState) => {
  dispatch(setLoading('owner', true));
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/clients/${clientOrgId}/completeness/sign-off`,
        JSON.stringify({}),
        orgHeader(getState()),
      ),
    );
    return { ok: true, data: response.data };
  } catch (e) {
    return parseApiError(e);
  } finally {
    dispatch(setLoading('owner', false));
  }
};

/**
 * Consultant Assist draft (R-9033+) — never auto-sends.
 */
export const requestAssist = (body) => async (dispatch, getState) => {
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        `${apiURL}/my/consultant/assist`,
        JSON.stringify(body || {}),
        orgHeader(getState()),
      ),
    );
    return { ok: true, draft: response.data?.draft, data: response.data };
  } catch (e) {
    return parseApiError(e);
  }
};

export const setOwnerFilter = (ownerUserId) => ({
  type: Types.SET_OWNER_FILTER,
  payload: ownerUserId || null,
});

export const Actions = {
  getConsultantDashboard,
  getClientHealth,
  getConsultantImpact,
  assignClientOwner,
  createWorkItem,
  listWorkItems,
  getWorkItem,
  updateWorkItem,
  postWorkItemMessage,
  requestAttachmentUpload,
  addWorkItemFiles,
  downloadWorkItemAttachment,
  removeWorkItemAttachment,
  renameWorkItemAttachment,
  createChaseDraft,
  approveChaseDraft,
  setRespondingPartyAccess,
  listProjects,
  createProject,
  updateProject,
  addTimeEntry,
  updateTimeEntry,
  listSharedAsks,
  getSharedAsk,
  updateSharedAsk,
  replySharedAsk,
  createSharedAsk,
  signOffCompleteness,
  requestAssist,
  setOwnerFilter,
};
